import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldAlert, ArrowRight, Copy } from "lucide-react";

/**
 * /preview-token?key=<ADMIN_KEY>&tier=<opportunity|executive|capture>
 *
 * Admin-only QA utility. Mints a one-use paid access token without going
 * through Stripe, injects it into localStorage, and lets the user open the wizard.
 */
export default function PreviewToken() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const adminKey = params.get("key") || "";
  const tier = params.get("tier") || "opportunity";
  const [state, setState] = useState({ status: "minting", error: null, data: null });
  const [verifiedInStorage, setVerifiedInStorage] = useState(false);

  useEffect(() => {
    if (!adminKey) {
      setState({ status: "error", error: "Missing ?key=<ADMIN_KEY>" });
      return;
    }
    let cancelled = false;
    async function mint() {
      try {
        const res = await api.post(
          `/admin/test-token?admin_key=${encodeURIComponent(adminKey)}&tier=${encodeURIComponent(tier)}`
        );
        if (cancelled) return;
        const { access_token, tier: t, session_id } = res.data;

        // Write to localStorage AND verify the write
        let wrote = false;
        try {
          localStorage.setItem(
            "govbidiq_access",
            JSON.stringify({ token: access_token, tier: t, session_id })
          );
          const readback = localStorage.getItem("govbidiq_access");
          wrote = !!readback && readback.includes(access_token);
        } catch (storageErr) {
          wrote = false;
        }

        setVerifiedInStorage(wrote);
        setState({ status: "ready", data: res.data });

        if (wrote) {
          toast.success(`Preview token activated successfully. ${t.charAt(0).toUpperCase() + t.slice(1)} tier active.`);
        } else {
          toast.error(
            "Token was issued but could not be saved to localStorage. " +
            "Check that cookies/site-data are not blocked, or copy the token manually below."
          );
        }
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || "Could not mint preview token" });
      }
    }
    mint();
    return () => {
      cancelled = true;
    };
  }, [adminKey, tier]);

  const copyToken = () => {
    if (!state.data?.access_token) return;
    try {
      navigator.clipboard.writeText(state.data.access_token);
      toast.success("Token copied to clipboard");
    } catch (_) {
      toast.error("Clipboard not available");
    }
  };

  const manualInject = () => {
    if (!state.data) return;
    try {
      localStorage.setItem(
        "govbidiq_access",
        JSON.stringify({
          token: state.data.access_token,
          tier: state.data.tier,
          session_id: state.data.session_id,
        })
      );
      const readback = localStorage.getItem("govbidiq_access");
      const ok = !!readback && readback.includes(state.data.access_token);
      setVerifiedInStorage(ok);
      if (ok) toast.success("Re-injected. Storage now confirmed.");
      else toast.error("Still cannot write to localStorage in this browser.");
    } catch (e) {
      toast.error("localStorage blocked: " + (e?.message || "unknown error"));
    }
  };

  return (
    <div className="grain min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-20">
        <div className="panel-elev p-8">
          {state.status === "minting" && (
            <>
              <Loader2 className="w-8 h-8 text-sky-400 animate-spin mb-5" />
              <div className="tick-label text-sky-400 mb-3">/ Issuing preview token</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
                Minting QA access token...
              </h1>
            </>
          )}

          {state.status === "ready" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-5" strokeWidth={1.5} />
              <div className="tick-label text-emerald-400 mb-3">/ Preview token ready</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
                Preview token activated successfully.
              </h1>
              <p className="text-slate-300 text-base mb-6">
                <span className="text-emerald-300 font-semibold capitalize">{state.data.tier}</span> tier is active.
                Token has been injected into your browser&apos;s localStorage.
              </p>

              <div className="bg-[var(--bg-secondary)] border border-slate-800 p-4 mb-6 font-mono text-xs space-y-2">
                <div className="text-slate-400">
                  tier: <span className="text-sky-400">{state.data.tier}</span>
                </div>
                <div className="text-slate-400 break-all">
                  access_token: <span className="text-emerald-400">{state.data.access_token}</span>
                </div>
                <div className="text-slate-400">
                  localStorage write:{" "}
                  <span className={verifiedInStorage ? "text-emerald-400" : "text-rose-400"}>
                    {verifiedInStorage ? "✓ confirmed" : "✗ blocked"}
                  </span>
                </div>
              </div>

              {verifiedInStorage ? (
                <>
                  <p className="text-sm text-slate-400 mb-5">
                    Continue to the wizard. The &ldquo;PAID ACCESS CONFIRMED&rdquo; banner should
                    appear at the top of the page.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      data-testid="preview-token-continue"
                      onClick={() => nav("/run-analysis")}
                      className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 transition-colors"
                    >
                      Open Wizard <ArrowRight className="w-4 h-4" strokeWidth={3} />
                    </button>
                    <button
                      onClick={copyToken}
                      data-testid="preview-token-copy"
                      className="inline-flex items-center gap-2 border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-3"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy Token
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-l-2 border-amber-500 bg-amber-500/5 p-4 mb-5">
                    <div className="tick-label text-amber-400 mb-1">/ Storage write blocked</div>
                    <p className="text-sm text-slate-200">
                      The token could not be saved. This is usually caused by incognito/private
                      browsing, blocked third-party cookies, or browser tracking-prevention
                      settings.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={manualInject}
                      data-testid="preview-token-retry"
                      className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-3"
                    >
                      Retry Storage Write
                    </button>
                    <button
                      onClick={copyToken}
                      className="inline-flex items-center gap-2 border border-slate-700 hover:border-sky-500 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-3"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy Token
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {state.status === "error" && (
            <>
              <ShieldAlert className="w-10 h-10 text-rose-400 mb-5" />
              <div className="tick-label text-rose-400 mb-3">/ Access Denied</div>
              <h1 className="font-display font-black text-2xl text-slate-100 mb-3">
                Could not mint preview token
              </h1>
              <p className="text-slate-400 text-sm mb-5">{state.error}</p>
              <p className="text-xs text-slate-500 font-mono">
                Usage: /preview-token?key=&lt;ADMIN_KEY&gt;&amp;tier=opportunity|executive|capture
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
